import { createI18n } from 'vue-i18n';
import type { PrimeVueConfiguration, PrimeVueLocaleOptions } from 'primevue/config';
import type { Language } from '@/i18n/languages';
import { PLATFORM_COUNTRIES, activePlatformCountry, type PlatformCountry } from '@/platformCountry';

type MessageModule = { default: Record<string, unknown> };
type MessageLoaders = Record<string, () => Promise<MessageModule>>;

// Messages are split by feature: one file per namespace, per language. The file name is the
// key's first segment — ./locales/pt/login.json holds the keys used as t('login.…').
// Layer 1: generic UI text, by language.
const coreMessageLoaders = import.meta.glob<MessageModule>('./locales/*/*.json');
// Layer 2 (optional): platform-country-specific wording that overrides or extends the core text, with the
// same layout, e.g. ./platform-countries/br/locales/pt/sar.json. Only keys that differ per platform belong here.
const platformCountryMessageLoaders = import.meta.glob<MessageModule>('./platform-countries/*/locales/*/*.json');

// PrimeVue ships its own built-in texts (empty states, date pickers, password hints…).
const primeVueLocaleLoaders: Record<Language, () => Promise<PrimeVueLocaleOptions>> = {
  pt: () => import('primelocale/pt-BR.json').then((module) => module.default['pt-BR']),
  es: () => import('primelocale/es.json').then((module) => module.default.es),
};
let primeVueConfig: PrimeVueConfiguration | null = null;

export const i18n = createI18n({
  legacy: false,
  locale: PLATFORM_COUNTRIES[activePlatformCountry.value].language,
  messages: {},
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV,
});

// Loads every namespace file under `directory` into { [namespace]: messages }.
async function loadNamespaces(loaders: MessageLoaders, directory: string): Promise<Record<string, unknown>> {
  const namespaceFiles = Object.entries(loaders).filter(([path]) => path.startsWith(directory));
  const namespaces = await Promise.all(
    namespaceFiles.map(async ([path, load]) => {
      const namespace = path.slice(directory.length).replace(/\.json$/, '');
      return [namespace, (await load()).default] as const;
    }),
  );
  return Object.fromEntries(namespaces);
}

// Rebuilds the active messages for a platform country: its language's core text plus its own overrides.
// Always rebuilt (not cached per language) because two platforms may share a language but not wording.
export async function applyPlatformCountryMessages(platformCountry: PlatformCountry): Promise<void> {
  const language = PLATFORM_COUNTRIES[platformCountry].language;

  const coreMessages = await loadNamespaces(coreMessageLoaders, `./locales/${language}/`);
  if (Object.keys(coreMessages).length === 0) {
    throw new Error(`Missing core message bundle for language "${language}".`);
  }
  const platformCountryMessages = await loadNamespaces(
    platformCountryMessageLoaders,
    `./platform-countries/${platformCountry}/locales/${language}/`,
  );
  const primeVueLocale = await primeVueLocaleLoaders[language]();

  i18n.global.setLocaleMessage(language, coreMessages);
  i18n.global.mergeLocaleMessage(language, platformCountryMessages);
  i18n.global.locale.value = language;
  document.documentElement.lang = language;
  if (primeVueConfig) {
    primeVueConfig.locale = { ...primeVueConfig.locale, ...primeVueLocale };
  }
}

// Call once at startup, after PrimeVue is installed, so its built-in texts follow the UI language.
export async function initializeI18n(installedPrimeVueConfig: PrimeVueConfiguration): Promise<void> {
  primeVueConfig = installedPrimeVueConfig;
  await applyPlatformCountryMessages(activePlatformCountry.value);
}
